// src/lib/api/jobs/media.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId, getValidatedUserId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired } from '../shared/validation'

/**
 * Get job media
 * Migrated from supabaseApi.js lines 1852-1870 (approximate)
 */
const _getJobMedia = async (jobId, companyId = null) => {
  validateRequired(jobId, 'Job ID')
  
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('job_media')
    .select('*')
    .eq('company_id', resolvedCompanyId)
    .eq('job_id', jobId)
    .order('created_at', { ascending: false })
  
  if (error) throw error
  return createSuccessResponse(data || [])
}

/**
 * Upload job media
 * Migrated from supabaseApi.js lines 1872-1890 (approximate)
 */
const _uploadJobMedia = async (mediaData) => {
  validateRequired(mediaData.job_id, 'Job ID')
  validateRequired(mediaData.file, 'File')
  validateRequired(mediaData.file_name, 'File name')
  
  const companyId = mediaData.company_id || await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const userId = await getValidatedUserId()
  if (!userId) throw new Error('Not authenticated')

  // *** CHECK MEDIA UPLOAD FEATURE ACCESS (Free tier: no media uploads except AI messages) ***
  const { data: featureCheck, error: featureError } = await supabase.rpc('has_plan_feature_access', {
    p_company_id: companyId,
    p_feature_name: 'media_uploads_enabled'
  })

  if (featureError) {
    throw new Error(`Failed to check media upload access: ${featureError.message}`)
  }

  if (!featureCheck || !featureCheck[0]?.has_access) {
    throw new Error('Media uploads are not available on the Free plan. Upgrade to Paid plan to upload before/after photos and job checklist media.')
  }

  // *** CHECK STORAGE RESTRICTION ***
  const fileSizeGB = mediaData.file.size / (1024 * 1024 * 1024)
  
  const { data: checkData, error: checkError } = await supabase.rpc('check_restriction', {
    p_company_id: companyId,
    p_restriction_name: 'storage_gb',
    p_increment_amount: fileSizeGB,
    p_action_name: 'upload_job_media'
  })

  if (checkError) {
    throw new Error(`Failed to check storage restrictions: ${checkError.message}`)
  }

  const restrictionCheck = checkData[0]
  if (!restrictionCheck.allowed) {
    throw new Error(`Storage limit exceeded: ${restrictionCheck.message}`)
  }
  
  // Upload file to storage. Bucket name is already specified via .from(),
  // so the path itself doesn't repeat it.
  const fileExtension = (mediaData.file_name?.split('.').pop() || 'jpg').toLowerCase()
  const randomId = Math.random().toString(36).slice(2)
  const filePath = `${companyId}/${mediaData.job_id}/${Date.now()}-${randomId}.${fileExtension}`
  const { data: uploadData, error: uploadError } = await supabase.storage
    .from('job-media')
    .upload(filePath, mediaData.file)
  
  if (uploadError) throw uploadError
  
  // Save media record
  const { data, error } = await supabase
    .from('job_media')
    .insert([{
      company_id: companyId,
      job_id: mediaData.job_id,
      file_path: filePath,
      file_name: mediaData.file_name,
      file_size: mediaData.file.size,
      mime_type: mediaData.file.type,
      description: mediaData.description || null,
      created_by: userId
    }])
    .select()
    .single()

  if (error) throw error

  // *** INCREMENT STORAGE USAGE AFTER SUCCESS ***
  try {
    const { error: incrementError } = await supabase.rpc('increment_usage', {
      p_company_id: companyId,
      p_restriction_name: 'storage_gb',
      p_amount: fileSizeGB
    })

    if (incrementError) {
      console.error('Failed to increment storage usage:', incrementError)
      // Don't fail the request, just log it
    }
  } catch (error) {
    console.error('Error incrementing storage usage:', error)
  }

  return data
}

/**
 * Delete job media
 * Migrated from supabaseApi.js lines 1892-1910 (approximate)
 */
const _deleteJobMedia = async (mediaId) => {
  validateRequired(mediaId, 'Media ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  // Get media record first (including file size for storage decrement)
  const { data: media, error: fetchError } = await supabase
    .from('job_media')
    .select('file_path, file_size')
    .eq('id', mediaId)
    .eq('company_id', companyId)
    .single()
  
  if (fetchError) throw fetchError
  
  // Delete from storage
  if (media.file_path) {
    const { error: storageError } = await supabase.storage
      .from('job-media')
      .remove([media.file_path])
    
    if (storageError) {
      console.warn('Failed to delete file from storage:', storageError)
    }
  }
  
  // Delete from database
  const { error } = await supabase
    .from('job_media')
    .delete()
    .eq('id', mediaId)
    .eq('company_id', companyId)
  
  if (error) throw error

  // *** DECREMENT STORAGE USAGE AFTER SUCCESSFUL DELETION ***
  if (media.file_size) {
    try {
      const fileSizeGB = media.file_size / (1024 * 1024 * 1024)
      const { error: decrementError } = await supabase.rpc('decrement_usage', {
        p_company_id: companyId,
        p_restriction_name: 'storage_gb',
        p_amount: fileSizeGB
      })

      if (decrementError) {
        console.error('Failed to decrement storage usage:', decrementError)
        // Don't fail the request, just log it
      }
    } catch (error) {
      console.error('Error decrementing storage usage:', error)
    }
  }

  return { success: true }
}

/**
 * Upload checklist image
 * Migrated from supabaseApi.js lines 1912-1930 (approximate)
 */
const _uploadChecklistImage = async (imageData) => {
  validateRequired(imageData.item_id, 'Item ID')
  validateRequired(imageData.file, 'File')
  
  const companyId = imageData.company_id || await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const userId = await getValidatedUserId()
  if (!userId) throw new Error('Not authenticated')

  // *** CHECK MEDIA UPLOAD FEATURE ACCESS (Free tier: no media uploads except AI messages) ***
  const { data: featureCheck, error: featureError } = await supabase.rpc('has_plan_feature_access', {
    p_company_id: companyId,
    p_feature_name: 'media_uploads_enabled'
  })
  
  if (featureError) {
    throw new Error(`Failed to check media upload access: ${featureError.message}`)
  }
  
  if (!featureCheck || !featureCheck[0]?.has_access) {
    throw new Error('Media uploads are not available on the Free plan. Upgrade to Paid plan to upload before/after photos and job checklist media.')
  }
  
  // Upload file to storage
  const filePath = `checklist-images/${companyId}/${imageData.item_id}/${Date.now()}-${imageData.file.name}`
  const { data: uploadData, error: uploadError } = await supabase.storage
    .from('checklist-images')
    .upload(filePath, imageData.file)
  
  if (uploadError) throw uploadError
  
  // Save attachment record
  const { data, error } = await supabase
    .from('job_checklist_attachments')
    .insert([{
      company_id: companyId,
      item_id: imageData.item_id,
      path: filePath,
      mime_type: imageData.file.type,
      size_bytes: imageData.file.size,
      created_by: userId
    }])
    .select()
    .single()
  
  if (error) throw error
  return data
}

/**
 * Get media by type
 */
const _getJobMediaByType = async (jobId, mediaType, companyId = null) => {
  validateRequired(jobId, 'Job ID')
  validateRequired(mediaType, 'Media type')
  
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('job_media')
    .select('*')
    .eq('company_id', resolvedCompanyId)
    .eq('job_id', jobId)
    .eq('media_type', mediaType)
    .order('created_at', { ascending: false })
  
  if (error) throw error
  return createSuccessResponse(data || [])
}

/**
 * Get signed URL for media
 */
const _getMediaSignedUrl = async (mediaId, expiresIn = 3600) => {
  validateRequired(mediaId, 'Media ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { data: media, error: fetchError } = await supabase
    .from('job_media')
    .select('file_path')
    .eq('id', mediaId)
    .eq('company_id', companyId)
    .single()
  
  if (fetchError) throw fetchError
  
  const { data: signedUrl, error: urlError } = await supabase.storage
    .from('job-media')
    .createSignedUrl(media.file_path, expiresIn)
  
  if (urlError) throw urlError
  return { url: signedUrl.signedUrl }
}

// Export wrapped functions with error handling
export const getJobMedia = withErrorHandling(_getJobMedia, 'fetch job media', 'job_media')
export const uploadJobMedia = withErrorHandling(_uploadJobMedia, 'upload job media', 'job_media')
export const deleteJobMedia = withErrorHandling(_deleteJobMedia, 'delete job media', 'job_media')
export const uploadChecklistImage = withErrorHandling(_uploadChecklistImage, 'upload checklist image', 'checklist_attachment')
export const getJobMediaByType = withErrorHandling(_getJobMediaByType, 'fetch job media by type', 'job_media')
export const getMediaSignedUrl = withErrorHandling(_getMediaSignedUrl, 'get media signed URL', 'job_media')